import * as React from "react";
import { Suspense, lazy, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { OfflineIndicator } from "@/components/OfflineIndicator";
import { OfflineSync } from "@/components/OfflineSync";
import { LaunchBanner } from "@/components/LaunchBanner";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { CartProvider } from "@/hooks/useCartSync";
import { CurrencyProvider } from "@/hooks/useCurrency";
import { SelectedRelayProvider } from "@/hooks/useSelectedRelay";
import { LanguageProvider } from "@/hooks/useLanguage";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import ProtectedRoute from "@/components/ProtectedRoute";
import ErrorBoundary from "@/components/ErrorBoundary";
import { useVisitorTracking } from "@/hooks/useVisitorTracking";
import { usePresenceHeartbeat } from "@/hooks/usePresenceHeartbeat";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { useServiceWorker } from "@/hooks/useServiceWorker";
import { useDeepLinking } from "@/hooks/useDeepLinking";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import EmailConfirmed from "./pages/EmailConfirmed";
import Dashboard from "./pages/Dashboard";
import VendorDashboard from "./pages/VendorDashboard";
import VendorAccount from "./pages/VendorAccount";
import VendorOrderDetail from "./pages/VendorOrderDetail";
import RelayDashboard from "./pages/RelayDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import BecomeVendor from "./pages/BecomeVendor";
import RelayPoints from "./pages/RelayPoints";
import CategoryPage from "./pages/CategoryPage";
import ProductDetail from "./pages/ProductDetail";
import AllProducts from "./pages/AllProducts";
import Orders from "./pages/Orders";
import OrderDetail from "./pages/OrderDetail";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCanceled from "./pages/PaymentCanceled";
import Deals from "./pages/Deals";
import PromotionPage from "./pages/PromotionPage";
import Maintenance from "./pages/Maintenance";
import NotFound from "./pages/NotFound";
import About from "./pages/About";
import HowItWorks from "./pages/HowItWorks";
import Support from "./pages/Support";
import SearchPage from "./pages/SearchPage";
import RobotsTxt from "./pages/RobotsTxt";
import SitemapXml from "./pages/SitemapXml";
import MyReviews from "./pages/MyReviews";
import Cart from "./components/Cart";
import MobileBottomNav from "./components/MobileBottomNav";
import MFAPage from "./pages/MFAPage";
import { MFAGuard } from "./components/MFAGuard";
import MFAPage from "./pages/MFAPage";
import { MFAGuard } from "./components/MFAGuard";

// Lazy load legal pages with code splitting for better isolation
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const LegalNotice = lazy(() => import("./pages/LegalNotice"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));

// Regular import for auth-related pages
import ForgotPassword from "./pages/ForgotPassword";

// Loading fallback for lazy-loaded components
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
  </div>
);

const queryClient = new QueryClient();

const MainRoutes = () => {
  const { settings, loading } = useSiteSettings();
  
  // Track visitor analytics
  useVisitorTracking();
  
  // Update user presence (last_seen_at) for online status
  usePresenceHeartbeat();
  
  // Initialize push notifications for mobile app
  usePushNotifications();

  // Initialize Service Worker for offline support
  useServiceWorker();

  // Initialize deep linking for mobile app
  useDeepLinking();

  // Splash screen is now auto-hidden by Capacitor config after 2 seconds

  // Show loading state while checking maintenance mode
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // If maintenance mode is enabled, show maintenance page for all non-admin routes
  if (settings.maintenance_mode) {
    return (
      <Routes>
        {/* Allow admin access during maintenance */}
        <Route 
          path="/admin" 
          element={
            <ProtectedRoute allowedRoles={['admin', 'hr', 'super_admin']}>
              <AdminDashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/vendor" 
          element={
            <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
              <VendorDashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/auth" 
          element={<Auth />} 
        />
        {/* All other routes show maintenance page - admins will be auto-redirected */}
        <Route path="*" element={<Maintenance />} />
      </Routes>
    );
  }

  // Normal routes when not in maintenance
  return (
    <Routes>
      {/* MFA page (enrollment / verification) */}
      <Route path="/mfa" element={<MFAPage />} />

      {/* SEO Static Files */}
      <Route path="/robots.txt" element={<RobotsTxt />} />
      <Route path="/sitemap.xml" element={<SitemapXml />} />
      
      <Route path="/" element={<Index />} />
      <Route path="/auth" element={<Auth />} />
      <Route path="/auth/confirmed" element={<EmailConfirmed />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/become-vendor" element={<BecomeVendor />} />
      <Route path="/relay-points" element={<RelayPoints />} />
      <Route path="/search" element={<SearchPage />} />
      <Route path="/category/:category" element={<CategoryPage />} />
      <Route path="/product/:id" element={<ProductDetail />} />
      <Route path="/products" element={<AllProducts />} />
      <Route path="/orders" element={<Orders />} />
      <Route path="/orders/:orderId" element={<OrderDetail />} />
      <Route path="/my-reviews" element={<MyReviews />} />
      <Route path="/deals" element={<Deals />} />
      <Route path="/promo/:adId" element={<PromotionPage />} />
      <Route path="/payment-success" element={<PaymentSuccess />} />
      <Route path="/payment-canceled" element={<PaymentCanceled />} />
      <Route path="/about" element={<About />} />
      <Route path="/how-it-works" element={<HowItWorks />} />
      <Route path="/support" element={<Support />} />
      <Route 
        path="/terms" 
        element={
          <ErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <TermsOfService />
            </Suspense>
          </ErrorBoundary>
        } 
      />
      <Route 
        path="/privacy" 
        element={
          <ErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <PrivacyPolicy />
            </Suspense>
          </ErrorBoundary>
        } 
      />
      <Route 
        path="/legal" 
        element={
          <ErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <LegalNotice />
            </Suspense>
          </ErrorBoundary>
        } 
      />
      <Route 
        path="/unsubscribe" 
        element={
          <ErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <Unsubscribe />
            </Suspense>
          </ErrorBoundary>
        } 
      />
      <Route 
        path="/dashboard"
        element={
          <ProtectedRoute allowedRoles={['client', 'admin', 'hr', 'super_admin', 'vendor', 'relay']}>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/vendor-account" 
        element={
          <ProtectedRoute allowedRoles={['vendor', 'admin', 'super_admin']}>
            <VendorAccount />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/vendor-account/orders/:orderId" 
        element={
          <ProtectedRoute allowedRoles={['vendor', 'admin', 'super_admin']}>
            <VendorOrderDetail />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/relay" 
        element={
          <ProtectedRoute allowedRoles={['relay', 'admin', 'super_admin']}>
            <RelayDashboard />
          </ProtectedRoute>
        } 
      />
      <Route path="/mfa" element={<MFAPage />} />
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'hr', 'super_admin']}>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <CurrencyProvider>
          <SelectedRelayProvider>
            <TooltipProvider>
              <AuthProvider>
                <CartProvider>
                  <MFAGuard>
                    <LaunchBanner />
                    <OfflineIndicator />
                    <OfflineSync />
                    <MainRoutes />
                    <MobileBottomNav />
                    <Cart />
                    <Toaster />
                    <Sonner />
                  </MFAGuard>
                </CartProvider>
              </AuthProvider>
            </TooltipProvider>
          </SelectedRelayProvider>
        </CurrencyProvider>
      </LanguageProvider>
    </QueryClientProvider>
  );
};

export default App;
